import type { CustomerRestrictionInput, CustomerRestrictionView } from '@chukua/common';
import { CUSTOMER_RESTRICTION_LABELS } from '@chukua/common';
import { Button, Popconfirm, Tag } from 'antd';
import { Ban, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { RowListSkeleton } from '../../../shared/components/RowListSkeleton';
import { useCustomerRestrictions } from '../hooks/use-customer-restrictions';
import { CustomerRestrictionModal } from './CustomerRestrictionModal';

interface CustomerRestrictionsTabProps {
  customerId: string;
  canManage: boolean;
}

/** What this customer will not accept on their cargo, checked when a truck is picked for one of their jobs (gitaumoses4/chukua#48). */
export function CustomerRestrictionsTab({ customerId, canManage }: CustomerRestrictionsTabProps) {
  const { state, saveError, clearSaveError, save, remove } = useCustomerRestrictions(customerId);
  const [editing, setEditing] = useState<CustomerRestrictionView | 'new' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (state.status === 'loading') return <RowListSkeleton label="Loading restrictions" rows={2} />;
  if (state.status === 'error') return <EmptyState icon={Ban} title="Couldn't load restrictions" description="Something went wrong. Try reloading the page." />;

  async function submit(input: CustomerRestrictionInput) {
    setSubmitting(true);
    try {
      const saved = await save(editing === 'new' || editing === null ? null : editing.id, input);
      if (saved) setEditing(null);
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    clearSaveError();
    setEditing(null);
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-fg">Restrictions</h2>
        {canManage && (
          <Button size="small" icon={<Plus className="size-3.5" />} onClick={() => setEditing('new')}>
            Add restriction
          </Button>
        )}
      </div>
      {state.restrictions.length === 0 && (
        <EmptyState
          icon={Ban}
          title="No restrictions"
          description="Add a rule, such as a maximum truck age, that trucks carrying this customer's cargo must meet."
        />
      )}
      {state.restrictions.length > 0 && (
        <ul className="flex flex-col gap-3">
          {state.restrictions.map((restriction) => (
            <RestrictionRow key={restriction.id} restriction={restriction} canManage={canManage} onEdit={() => setEditing(restriction)} onRemove={remove} />
          ))}
        </ul>
      )}
      <CustomerRestrictionModal
        open={editing !== null}
        restriction={editing === 'new' ? null : editing}
        submitting={submitting}
        error={saveError}
        onSubmit={submit}
        onCancel={close}
      />
    </section>
  );
}

function RestrictionRow({
  restriction,
  canManage,
  onEdit,
  onRemove,
}: {
  restriction: CustomerRestrictionView;
  canManage: boolean;
  onEdit: () => void;
  onRemove: (restriction: CustomerRestrictionView) => Promise<void>;
}) {
  const label = CUSTOMER_RESTRICTION_LABELS[restriction.kind];
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <div className="flex items-center gap-3">
        <p className="font-medium text-fg">
          {label}: {restriction.params.years}
        </p>
        <Tag color={restriction.isAdvisory ? 'warning' : 'error'}>{restriction.isAdvisory ? 'Advisory' : 'Blocks'}</Tag>
      </div>
      {canManage && (
        <div className="flex items-center gap-1">
          <Button type="text" size="small" icon={<Pencil className="size-3.5" />} onClick={onEdit} aria-label={`Edit ${label}`} />
          <Popconfirm title={`Remove ${label}?`} onConfirm={() => onRemove(restriction)}>
            <Button type="text" size="small" danger icon={<Trash2 className="size-3.5" />} aria-label={`Remove ${label}`} />
          </Popconfirm>
        </div>
      )}
    </li>
  );
}
